import {useRef, useCallback, useEffect} from 'react';
import {useAiChatActions} from '@/ai-chat/providers/ai-chat.context';
import {getPosterHashedIdForAiChatImageAsset} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {showMessageGrowl, updateGrowlProgress, GROWL_TYPE} from '@Components/message-growl';
import {LoadingStates} from '@Utils/loading.util';
import {GA4EventName} from '@Libraries/ga-events';

const SAVE_GROWL_KEY = 'save-to-my-designs';
const PROGRESS_DURATION_MS = 15000;
const PROGRESS_STEPS = 60;
const PROGRESS_MAX = 90;
const GROWL_LOADING_INTERVAL = 120000;

interface UseSaveToMyDesignsReturn {
  onSaveToMyDesigns: () => Promise<void>;
}

export function useSaveToMyDesigns(chatId: string, hashedId: string): UseSaveToMyDesignsReturn {
  const actions = useAiChatActions();
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearProgressInterval = (): void => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const startSmoothProgress = (): void => {
    let progress = 0;
    const intervalTime = PROGRESS_DURATION_MS / PROGRESS_STEPS;
    const progressIncrement = PROGRESS_MAX / PROGRESS_STEPS;

    progressIntervalRef.current = setInterval(() => {
      progress += progressIncrement;
      const clampedProgress = Math.min(progress, PROGRESS_MAX);
      updateGrowlProgress(SAVE_GROWL_KEY, clampedProgress);

      if (progress >= PROGRESS_MAX) {
        clearProgressInterval();
      }
    }, intervalTime);
  };

  useEffect(() => {
    return (): void => {
      clearProgressInterval();
    };
  }, []);

  const onSaveToMyDesigns = useCallback(async (): Promise<void> => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_SAVE_TO_MY_DESIGNS);
    actions.updateLoadingStatesForImageActions(chatId, hashedId, {saveToMyDesignsLoadingState: LoadingStates.LOADING});

    showMessageGrowl({
      key: SAVE_GROWL_KEY,
      text: window.i18next.t('pmwjs_saving_to_my_designs'),
      type: GROWL_TYPE.NEUTRAL,
      showCloseIcon: true,
      interval: GROWL_LOADING_INTERVAL,
      progress: 0,
    });

    startSmoothProgress();

    try {
      await getPosterHashedIdForAiChatImageAsset(hashedId);

      clearProgressInterval();

      showMessageGrowl({
        key: SAVE_GROWL_KEY,
        text: window.i18next.t('pmwjs_file_saved'),
        type: GROWL_TYPE.SUCCESS,
      });
    } catch (e: unknown) {
      clearProgressInterval();
      console.error(e);

      showMessageGrowl({
        key: SAVE_GROWL_KEY,
        text: window.i18next.t('pmwjs_something_went_wrong'),
        type: GROWL_TYPE.DANGER,
      });
    } finally {
      actions.updateLoadingStatesForImageActions(chatId, hashedId, {saveToMyDesignsLoadingState: LoadingStates.LOADED});
    }
  }, [chatId, hashedId, actions]);

  return {onSaveToMyDesigns};
}
